/**
 * IT Operations Dashboard
 * Main dashboard for IT Manager and IT Support roles
 * Features: KPI tiles, tickets management, tasks, tech availability, SLA tracking
 * Real-time notifications via WebSocket + Toast alerts
 * IT Asset Management module
 * 
 * REFACTORED: UI components extracted to /components/it-dashboard/
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  AlertCircle, 
  Clock, 
  CheckCircle, 
  PauseCircle, 
  UserPlus,
  RefreshCw,
  Plus,
  Trash2,
  Search,
  Filter,
  Bell,
  Megaphone,
  Calendar,
  X,
  Edit2,
  ExternalLink,
  AlertTriangle,
  Timer,
  TrendingUp,
  Wifi,
  WifiOff,
  Paperclip,
  Download,
  FileText,
  Image,
  Video,
  File,
  Eye,
  Laptop,
  Smartphone,
  HardDrive,
  Package,
  QrCode,
  UserCheck,
  Wrench,
  Archive,
  UserMinus,
  FileSignature,
  Camera,
  Layers,
  Printer,
  Pencil,
  Ticket,
  Users,
  FileSpreadsheet,
  List as ListIcon
} from 'lucide-react';
import { toast } from 'sonner';
import AssetsByEmployee from '../components/it/AssetsByEmployee';
import './ITDashboard.css';
import './ITDashboard-theme.css';
import './ITDashboard-premium.css';
import { formatDateShortET, formatTimeOnlyET, formatDateOnly } from '../utils/formatters';

// Import Session Components for Break/Active status

// Import Downtime Analytics for IT Operations
import DowntimeAnalytics from '../components/analytics/DowntimeAnalytics';

// Import Asset Management Components
import MaintenanceModal from '../components/assets/MaintenanceModal';
import OffboardingModal from '../components/assets/OffboardingModal';
import OffboardingCaseModal from '../components/assets/OffboardingCaseModal';
import AcknowledgmentModal from '../components/assets/AcknowledgmentModal';
import QRScannerModal from '../components/assets/QRScannerModal';
import AnnouncementReadsModal from '../components/it-dashboard/AnnouncementReadsModal';
import ReportsPanel from '../components/assets/ReportsPanel';
import AuditLogPanel from '../components/assets/AuditLogPanel';
import '../components/assets/assets.css';

// Import Credentials Module (Secrets Vault)
import CredentialsPanel from '../components/credentials/CredentialsPanel';
import '../components/credentials/credentials.css';

// Import Refactored IT Dashboard Components
import {
  KPITile,
  PriorityBadge,
  StatusBadge,
  SLABadge,
  AttachmentIcon,
  formatFileSize,
  AvailabilityIndicator
} from '../components/it-dashboard/UIComponents';

import {
  TicketDetailModal,
  CompleteTaskModal,
  TaskDetailModal,
  AddTaskModal,
  AddIncidentModal,
  AddAnnouncementModal,
  AddReminderModal,
  SLAComplianceCard
} from '../components/it-dashboard/Modals';

import {
  AddAssetModal,
  EditAssetModal,
  AssignAssetModal,
  AssetDetailModal
} from '../components/it-dashboard/AssetModals';
import { ProductTypesModal } from '../components/it-dashboard/ProductTypesModal';
import ITSidebar from '../components/it/ITSidebar';
import ITPagination from '../components/it/ITPagination';
import ConfirmActionModal from '../components/it/ConfirmActionModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get auth token
const getToken = () => localStorage.getItem('token');

// Construct WebSocket URL from API URL
const getWebSocketUrl = () => {
  const token = getToken();
  if (!token) return null;
  
  // Convert http(s) to ws(s)
  const wsProtocol = API_URL.startsWith('https') ? 'wss' : 'ws';
  const wsUrl = API_URL.replace(/^https?/, wsProtocol);
  return `${wsUrl}/api/it-notifications/ws?token=${token}`;
};

// API helper
const apiCall = async (endpoint, options = {}) => {
  const token = getToken();
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  
  return response.json();
};

/* IT-DASH-PREMIUM: time-aware greeting */
const itGreeting = (d) => {
  const h = d.getHours();
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  return 'Good Evening';
};

const ITDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  // IT-NAV-REDESIGN: dedicated IT Requests workspace + shared unified sidebar
  const [ticketSearch, setTicketSearch] = useState('');
  // IT-PAGINATION-001: server-side search + rows-per-page for IT Requests
  const [ticketQuery, setTicketQuery] = useState('');
  const [ticketLimit, setTicketLimit] = useState(25);
  // Tasks tab pagination (separate from dashboard widget data)
  const [taskList, setTaskList] = useState([]);
  const [taskPage, setTaskPage] = useState(1);
  const [taskLimit, setTaskLimit] = useState(25);
  const [taskTotal, setTaskTotal] = useState(0);
  const [taskStatusFilter, setTaskStatusFilter] = useState('all');
  const [completeTaskTarget, setCompleteTaskTarget] = useState(null);
  const [taskDetailTarget, setTaskDetailTarget] = useState(null);
  const [assetLimit, setAssetLimit] = useState(25);

  // IT-UNIFIED-NAV: honor ?tab= deep links from the standalone sidebar
  // (e.g. /admin/it-dashboard?tab=requests when coming from /admin/users)
  const location = useLocation();
  useEffect(() => {
    const t = new URLSearchParams(location.search).get('tab');
    if (t) setActiveTab(t);
  }, [location.search]);
  // PORTAL-CONTEXT-001: entering the IT Operations Center marks the IT
  // portal context (per-tab). AdminLayout/embedded modules read it so an
  // Admin browsing IT stays inside the dark IT shell on /admin/team and
  // /admin/workspace until they explicitly leave via Admin Home.
  useEffect(() => {
    try { sessionStorage.setItem('titleflow-portal-context', 'it'); } catch (_) { /* noop */ }
  }, []);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // IT-DASH-PREMIUM — live clock for greeting pill (30s tick) + real API health
  const [itNow, setItNow] = useState(new Date());
  const [apiHealth, setApiHealth] = useState(null);
  useEffect(() => {
    const t = setInterval(() => setItNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  
  // Data states
  const [kpis, setKpis] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tickets, setTickets] = useState([]);
  // IT-NAV-REDESIGN fix: dashboard preview has its own state so workspace
  // filter fetches never pollute the Recent Requests panel.
  const [recentTickets, setRecentTickets] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [slaMetrics, setSlaMetrics] = useState(null);
  const [breachedTickets, setBreachedTickets] = useState([]);
  
  // Asset Management states
  const [assets, setAssets] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [showProductTypes, setShowProductTypes] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [assetStats, setAssetStats] = useState(null);
  const [assetFilter, setAssetFilter] = useState('all');
  const [assetWarranty, setAssetWarranty] = useState(null); // null | '30' | '60' | '90' | 'expired'
  const [assetSearch, setAssetSearch] = useState('');
  // IT-ASSET-EMPLOYEE-GROUPING-001
  const [assetView, setAssetView] = useState('assets'); // 'assets' | 'employees'
  const [employeeGroups, setEmployeeGroups] = useState([]);
  const [employeeTotals, setEmployeeTotals] = useState({ employees: 0, assets: 0 });
  const [employeeGroupsLoading, setEmployeeGroupsLoading] = useState(false);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [exportingAssets, setExportingAssets] = useState(false);
  const [assetQuery, setAssetQuery] = useState('');
  const [assetPage, setAssetPage] = useState(1);
  const [assetPageInfo, setAssetPageInfo] = useState({ total: 0, pages: 1 });
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [showAssignAsset, setShowAssignAsset] = useState(false);
  const [showAssetDetail, setShowAssetDetail] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  
  // New Asset Management modal states
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showOffboardingModal, setShowOffboardingModal] = useState(false);
  const [offboardingCases, setOffboardingCases] = useState([]);
  const [offboardingDetailCaseId, setOffboardingDetailCaseId] = useState(null);
  const [offboardInitialUser, setOffboardInitialUser] = useState(null);
  const [showAcknowledgmentModal, setShowAcknowledgmentModal] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  // Filter states
  const [ticketFilter, setTicketFilter] = useState('all');
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [showSlaView, setShowSlaView] = useState(false);
  
  // Modal states
  const [showAddTask, setShowAddTask] = useState(false);
  const [showAddIncident, setShowAddIncident] = useState(false);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  // NO-NATIVE-POPUPS-001: TitleFlow-styled confirmation replaces window.confirm/alert
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedTicketDetail, setSelectedTicketDetail] = useState(null);
  const [loadingTicketDetail, setLoadingTicketDetail] = useState(false);
  const [showAddAnnouncement, setShowAddAnnouncement] = useState(false);
  const [readsAnnouncementId, setReadsAnnouncementId] = useState(null);
  const [showAddReminder, setShowAddReminder] = useState(false);
  
  // Pagination
  const [ticketPage, setTicketPage] = useState(1);
  const [ticketTotal, setTicketTotal] = useState(0);
  const ticketsPerPage = ticketLimit;
  
  // WebSocket state
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;
  
  // Check if user is IT Manager
  // IT-SUPPORT-PERM-001: normalize the role so stale localStorage sessions
  // (e.g. "IT Support" display strings) can never break the gating.
  const normalizedRole = String(user?.role || '').toLowerCase().trim().replace(/\s+/g, '_');
  const isITManager = normalizedRole === 'it_manager' || normalizedRole === 'admin';
  // IT-SUPPORT-PERM-001 (Aug 24, 2026): IT Support may post announcements.
  const canPostAnnouncements = isITManager || normalizedRole === 'it_support';
  
  // Get user info from token
  useEffect(() => {
    const token = getToken();
    if (token) {
      try {
        // First try to get user from localStorage (set during login)
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          setUser({
            id: userData.id,
            email: userData.email,
            role: userData.role,
            name: userData.full_name || userData.email
          });
          return;
        }
        
        // Fallback to parsing token (may not have all fields)
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({
          id: payload.sub,
          email: payload.email,
          role: payload.role,
          name: payload.full_name || payload.email
        });
      } catch (e) {
        console.error('Failed to decode token:', e);
      }
    }
  }, []);
  
  // ============================================================================
  // HEARTBEAT: Keep session status as "Active" while using IT Dashboard
  // ============================================================================
  useEffect(() => {
    const sendHeartbeat = async () => {
      try {
        await apiCall('/api/session-health/heartbeat', { method: 'POST' });
      } catch (err) {
        console.error('Heartbeat failed:', err);
      }
    };
    
    // Send heartbeat immediately on mount
    sendHeartbeat();
    
    // Then send every 5 minutes to keep status active
    const interval = setInterval(sendHeartbeat, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // ============================================================================
  // WEBSOCKET CONNECTION FOR REAL-TIME NOTIFICATIONS
  // ============================================================================
  
  // Track if reconnecting banner should show (disconnected > 10s)
  const [showReconnectingBanner, setShowReconnectingBanner] = useState(false);
  const disconnectTimerRef = useRef(null);
  
  const connectWebSocket = useCallback(() => {
    const wsUrl = getWebSocketUrl();
    if (!wsUrl) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    console.log('[WS] Connecting to IT Dashboard notifications...');
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    
    ws.onopen = () => {
      console.log('[WS] Connected to IT Dashboard notifications');
      setWsConnected(true);
      reconnectAttemptsRef.current = 0;
      
      // Clear reconnecting banner if showing
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
      setShowReconnectingBanner(false);
      
      // No toast on connect - LIVE badge is sufficient
      // Only show once per session if needed (optional)
      // const hasShownConnected = sessionStorage.getItem('ws_connected_shown');
      // if (!hasShownConnected) {
      //   sessionStorage.setItem('ws_connected_shown', 'true');
      //   toast.success('Real-time notifications active', { duration: 2000 });
      // }
    };
    
    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        handleWebSocketMessage(message);
      } catch (e) {
        console.error('[WS] Failed to parse message:', e);
      }
    };
    
    ws.onclose = (event) => {
      console.log('[WS] Disconnected:', event.code, event.reason);
      setWsConnected(false);
      wsRef.current = null;
      
      // Show reconnecting banner after 10 seconds of disconnection
      if (event.code !== 1000) {
        disconnectTimerRef.current = setTimeout(() => {
          setShowReconnectingBanner(true);
        }, 10000);
      }
      
      // Attempt reconnection if not intentional close
      if (event.code !== 1000 && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        console.log(`[WS] Reconnecting in ${delay}ms...`);
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current++;
          connectWebSocket();
        }, delay);
      }
    };
    
    ws.onerror = (error) => {
      console.error('[WS] Error:', error);
    };
  }, []);
  
  // Handle incoming WebSocket messages
  const handleWebSocketMessage = useCallback((message) => {
    console.log('[WS] Received:', message.type, message);
    
    switch (message.type) {
      case 'connected':
        console.log('[WS] Welcome message:', message.message);
        break;
        
      case 'new_ticket':
        // Show toast notification for new ticket
        const ticket = message.data;
        const priorityEmoji = {
          urgent: '🚨',
          high: '🔴',
          medium: '🟡',
          low: '🟢'
        };
        const sourceLabel = ticket.source === 'it_incident' ? 'IT Incident' : 'User Request';
        
        toast.info(
          <div className="notification-toast">
            <div className="toast-header">
              <span>{priorityEmoji[ticket.priority] || '📋'} New {sourceLabel}</span>
              <span className="toast-ticket-id">{ticket.ticket_number}</span>
            </div>
            <div className="toast-title">{ticket.title}</div>
            <div className="toast-meta">
              From: {ticket.created_by_name} • {ticket.category}
            </div>
          </div>,
          {
            duration: 8000,
            action: {
              label: 'View',
              onClick: () => {
                setTicketFilter('all');
                fetchDashboardData();
              }
            }
          }
        );
        
        // Auto-refresh tickets list
        fetchDashboardData();
        break;
        
      case 'ticket_update':
        // Show update notification
        toast.info(`Ticket ${message.update_type}: ${message.data.ticket_id}`, {
          duration: 4000
        });
        fetchDashboardData();
        break;
        
      case 'kpi_update':
        // Update KPIs in real-time
        if (message.data) {
          setKpis(prev => ({ ...prev, ...message.data }));
        }
        break;
        
      case 'heartbeat':
      case 'pong':
        // Keep-alive messages, no action needed
        break;
        
      default:
        console.log('[WS] Unknown message type:', message.type);
    }
  }, []);
  
  // Connect WebSocket when component mounts
  useEffect(() => {
    const token = getToken();
    if (token) {
      // Delay connection slightly to ensure component is mounted
      const connectTimeout = setTimeout(() => {
        connectWebSocket();
      }, 1000);
      
      return () => {
        clearTimeout(connectTimeout);
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        if (disconnectTimerRef.current) {
          clearTimeout(disconnectTimerRef.current);
        }
        if (wsRef.current) {
          wsRef.current.close(1000, 'Component unmounting');
        }
      };
    }
  }, [connectWebSocket]);
  
  // Send ping to keep connection alive
  useEffect(() => {
    const pingInterval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Ping every 30 seconds
    
    return () => clearInterval(pingInterval);
  }, []);
  
  // Fetch all dashboard data
  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [kpisData, tasksData, ticketsData, techData, slaData, breachedData] = await Promise.all([
        apiCall('/api/it-dashboard/kpis'),
        apiCall('/api/it-dashboard/tasks'),
        apiCall('/api/it-dashboard/sla/tickets?limit=10'),  // Use SLA endpoint for tickets
        apiCall('/api/it-dashboard/technicians'),
        apiCall('/api/it-dashboard/sla/metrics?days=30'),
        apiCall('/api/it-dashboard/sla/breached')
      ]);
      
      setKpis(kpisData);
      setTasks(tasksData.tasks || []);
      setRecentTickets(ticketsData.tickets || []);
      setSlaMetrics(slaData.metrics || null);
      setBreachedTickets(breachedData.breached || []);
      setTechnicians(techData.technicians || []);
      
      // Fetch announcements and reminders (+ real API health for the summary card)
      try {
        const [announcementsData, remindersData, healthData] = await Promise.all([
          apiCall('/api/it-dashboard/announcements'),
          apiCall('/api/it-dashboard/reminders'),
          apiCall('/api/health').catch(() => null)
        ]);
        setAnnouncements(announcementsData.announcements || []);
        setReminders(remindersData.reminders || []);
        setApiHealth(healthData);
      } catch (e) {
        console.log('Optional data fetch failed:', e);
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchDashboardData();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);
  
  // Fetch tickets with filter (using SLA endpoint for enriched data)
  const fetchTickets = async (filter = 'all', page = 1, limitOverride = null, queryOverride = null) => {
    try {
      const lim = limitOverride || ticketLimit;
      const q = queryOverride !== null ? queryOverride : ticketQuery;
      let endpoint = `/api/it-dashboard/sla/tickets?page=${page}&limit=${lim}`;
      if (q && q.trim()) endpoint += `&search=${encodeURIComponent(q.trim())}`;
      if (filter === 'unassigned') {
        endpoint += '&unassigned=true';
      } else if (filter === 'breached') {
        endpoint += '&sla_status=breached';
      } else if (filter === 'at_risk') {
        endpoint += '&sla_status=at_risk';
      } else if (filter !== 'all') {
        endpoint += `&status=${filter}`;
      }
      
      const data = await apiCall(endpoint);
      setTickets(data.tickets || []);
      setTicketTotal(data.total || 0);
      setTicketPage(page);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    }
  };
  
  // Handle filter change
  const handleFilterChange = (filter) => {
    setTicketFilter(filter);
    setShowSlaView(filter === 'breached' || filter === 'at_risk');
    fetchTickets(filter, 1);
    setActiveTab('requests');
  };
  
  // Task actions
  const handleCreateTask = async (taskData) => {
    await apiCall('/api/it-dashboard/tasks', {
      method: 'POST',
      body: JSON.stringify(taskData)
    });
    fetchDashboardData();
    if (activeTab === 'tasks') fetchTaskList();
  };
  
  const handleDeleteTask = (taskId) => setConfirmAction({
    title: 'Delete Task?',
    message: 'This task will be permanently deleted. This action cannot be undone.',
    confirmLabel: 'Delete Task',
    run: async () => {
      await apiCall(`/api/it-dashboard/tasks/${taskId}`, { method: 'DELETE' });
      toast.success('Task deleted');
      fetchDashboardData();
      if (activeTab === 'tasks') fetchTaskList();
    },
  });
  
  const handleCompleteTask = async (taskId, notes) => {
    await apiCall(`/api/it-dashboard/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'completed', ...(notes ? { completion_notes: notes } : {}) })
    });
    toast.success('Task marked completed — retained under Completed history');
    setCompleteTaskTarget(null);
    fetchDashboardData();
    if (activeTab === 'tasks') fetchTaskList();
  };
  
  // Incident actions
  const handleCreateIncident = async (incidentData) => {
    await apiCall('/api/it-dashboard/tickets/incident', {
      method: 'POST',
      body: JSON.stringify(incidentData)
    });
    fetchDashboardData();
  };
  
  // Announcement handlers
  const handleCreateAnnouncement = async (announcementData) => {
    await apiCall('/api/it-dashboard/announcements', {
      method: 'POST',
      body: JSON.stringify(announcementData)
    });
    toast.success('Announcement posted successfully');
    fetchDashboardData();
  };
  
  const handleDeleteAnnouncement = (announcementId) => setConfirmAction({
    title: 'Delete Announcement?',
    message: 'This announcement will be removed for everyone in the IT portal.',
    confirmLabel: 'Delete Announcement',
    run: async () => {
      await apiCall(`/api/it-dashboard/announcements/${announcementId}`, { method: 'DELETE' });
      toast.success('Announcement deleted');
      fetchDashboardData();
    },
  });
  
  // Reminder handlers
  const handleCreateReminder = async (reminderData) => {
    await apiCall('/api/it-dashboard/reminders', {
      method: 'POST',
      body: JSON.stringify(reminderData)
    });
    toast.success('Reminder added');
    fetchDashboardData();
  };
  
  const handleCompleteReminder = async (reminderId) => {
    try {
      await apiCall(`/api/it-dashboard/reminders/${reminderId}/complete`, { method: 'PUT' });
      toast.success('Reminder completed');
      fetchDashboardData();
    } catch (err) {
      toast.error('Failed to complete reminder');
    }
  };
  
  const handleDeleteReminder = async (reminderId) => {
    try {
      await apiCall(`/api/it-dashboard/reminders/${reminderId}`, { method: 'DELETE' });
      fetchDashboardData();
    } catch (err) {
      toast.error('Failed to delete reminder');
    }
  };
  
  // ============================================================================
  // ASSET MANAGEMENT HANDLERS
  // ============================================================================
  
  const fetchAssets = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(assetPage), limit: String(assetLimit) });
      if (assetFilter !== 'all') params.set('status', assetFilter);
      if (assetWarranty) params.set('warranty', assetWarranty === 'expired' ? 'expired' : `expiring_${assetWarranty}`);
      if (assetQuery.trim()) params.set('search', assetQuery.trim());
      const [assetsData, statsData, typesData] = await Promise.all([
        apiCall(`/api/it-assets?${params}`),
        apiCall('/api/it-assets/stats').catch(() => null),
        apiCall('/api/it-asset-types').catch(() => ({ types: [] }))
      ]);
      setAssets(assetsData.assets || []);
      setAssetPageInfo({ total: assetsData.total || 0, pages: assetsData.pages || 1 });
      if (statsData) setAssetStats(statsData);
      setAssetTypes(typesData.types || []);
    } catch (err) {
      console.error('Failed to fetch assets:', err);
    }
  }, [assetFilter, assetWarranty, assetPage, assetQuery, assetLimit]);

  // debounce search into server query; reset to page 1 on search/filter change
  useEffect(() => {
    const t = setTimeout(() => { setAssetQuery(assetSearch); setAssetPage(1); }, 300);
    return () => clearTimeout(t);
  }, [assetSearch]);
  useEffect(() => { setAssetPage(1); }, [assetFilter, assetWarranty]);

  // IT-ASSET-EMPLOYEE-GROUPING-001 — employee groups are re-derived from live assignment
  // state whenever the asset list changes (assign / return / transfer) or filters move.
  const fetchEmployeeGroups = useCallback(async () => {
    setEmployeeGroupsLoading(true);
    try {
      const params = new URLSearchParams();
      if (assetFilter !== 'all') params.set('status', assetFilter);
      if (assetQuery.trim()) params.set('search', assetQuery.trim());
      const data = await apiCall(`/api/it-assets/by-employee?${params}`);
      setEmployeeGroups(data.employees || []);
      setEmployeeTotals({ employees: data.total_employees || 0, assets: data.total_assets || 0 });
    } catch (err) {
      console.error('Failed to fetch employee groups:', err);
    } finally {
      setEmployeeGroupsLoading(false);
    }
  }, [assetFilter, assetQuery]);
  useEffect(() => { if (assetView === 'employees') fetchEmployeeGroups(); }, [assetView, fetchEmployeeGroups, assets]);

  // IT-ASSET-EMPLOYEE-GROUPING-001 — shared by the Assets table and the By Employee view
  const renderAssetActions = (asset) => (
    <>
      <button 
        className="btn-small btn-view"
        onClick={() => viewAssetDetail(asset.id)}
        title="View Details"
      >
        <Eye size={14} />
      </button>
      {asset.status !== 'retired' && isITManager && (
        <button 
          className="btn-small"
          onClick={() => setEditingAsset(asset)}
          title="Edit"
          data-testid={`edit-asset-${asset.id}`}
        >
          <Pencil size={14} />
        </button>
      )}
      {asset.status === 'available' && isITManager && (
        <button 
          className="btn-small btn-assign"
          onClick={() => { setSelectedAsset(asset); setShowAssignAsset(true); }}
          title="Assign"
        >
          <UserPlus size={14} />
        </button>
      )}
      {(asset.status === 'assigned' || asset.status === 'pending_return') && (
        <button 
          className="btn-small btn-return"
          onClick={() => handleReturnAsset(asset.id, { condition_at_return: asset.condition })}
          title="Return"
        >
          <Archive size={14} />
        </button>
      )}
      {asset.status !== 'retired' && (
        <button 
          className="btn-small btn-maintenance"
          onClick={() => { setSelectedAsset(asset); setShowMaintenanceModal(true); }}
          title="Maintenance"
        >
          <Wrench size={14} />
        </button>
      )}
      {asset.status !== 'retired' && isITManager && (
        <button 
          className="btn-small btn-danger"
          onClick={() => handleRetireAsset(asset.id)}
          title="Retire"
        >
          <Trash2 size={14} />
        </button>
      )}
    </>
  );

  const handleExportAssets = async () => {
    setExportingAssets(true);
    try {
      const params = new URLSearchParams();
      if (assetFilter !== 'all') params.set('status', assetFilter);
      if (assetWarranty) params.set('warranty', assetWarranty === 'expired' ? 'expired' : `expiring_${assetWarranty}`);
      if (assetQuery.trim()) params.set('search', assetQuery.trim());
      const res = await fetch(`${API_URL}/api/it-assets/export?${params}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Export failed' }));
        throw new Error(err.detail || 'Export failed');
      }
      const blob = await res.blob();
      const name = (res.headers.get('content-disposition') || '').match(/filename="?([^";]+)"?/)?.[1] || 'IT_Asset_Inventory.xlsx';
      const url = URL.createObjectURL(blob);
      const a = Object.assign(document.createElement('a'), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export ready — ${res.headers.get('x-total-records') || ''} assets`);
    } catch (err) {
      toast.error(err.message || 'Export failed');
    } finally {
      setExportingAssets(false);
    }
  };

  // KPI card shortcuts — status cards clear the warranty filter and vice versa
  const selectAssetKpi = (status) => { setAssetWarranty(null); setAssetFilter(status); };
  const selectWarrantyKpi = () => { setAssetFilter('all'); setAssetWarranty('30'); };
  
  const fetchAllUsers = async () => {
    try {
      // it_manager lacks users.view — use the IT-scoped minimal directory
      const response = await apiCall('/api/it-assets/assignable-users');
      const users = Array.isArray(response) ? response : (response.users || []);
      setAllUsers(users);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };
  
  const handleCreateAsset = async (assetData) => {
    await apiCall('/api/it-assets', {
      method: 'POST',
      body: JSON.stringify(assetData)
    });
    toast.success('Asset created successfully');
    fetchAssets();
  };
  
  const handleUpdateAsset = async (assetId, assetData) => {
    await apiCall(`/api/it-assets/${assetId}`, {
      method: 'PUT',
      body: JSON.stringify(assetData)
    });
    toast.success('Asset updated');
    fetchAssets();
  };
  
  const handlePrintLabels = async () => {
    try {
      const data = await apiCall('/api/it-assets/labels' + (assetFilter !== 'all' ? `?status=${assetFilter}` : ''));
      const labels = data.labels || [];
      if (!labels.length) { toast.error('No assets to print'); return; }
      const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const qrSrc = (qc) => (qc && qc.startsWith('data:') ? qc : `data:image/png;base64,${qc}`);
      const w = window.open('', '_blank');
      w.document.write(`<!DOCTYPE html><html><head><title>QR Asset Labels</title><style>
        body { font-family: Arial, sans-serif; margin: 16px; }
        .sheet { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
        .label { border: 1px dashed #94a3b8; border-radius: 6px; padding: 8px; text-align: center; page-break-inside: avoid; }
        .label img { width: 96px; height: 96px; }
        .tag { font-weight: bold; font-size: 12px; font-family: monospace; }
        .name { font-size: 10px; color: #334155; margin-top: 2px; }
        .type { font-size: 9px; color: #64748b; }
        @media print { .no-print { display: none; } }
      </style></head><body>
      <div class="no-print" style="margin-bottom:12px"><button onclick="window.print()">Print</button> ${labels.length} labels</div>
      <div class="sheet">` + labels.map(l => `
        <div class="label">
          ${l.qr_code ? `<img src="${qrSrc(l.qr_code)}" alt="QR" />` : ''}
          <div class="tag">${esc(l.asset_tag)}</div>
          <div class="name">${esc((l.name || '').slice(0, 40))}</div>
          <div class="type">${esc(l.asset_type_name || l.category || '')}${l.serial_number ? ' · ' + esc(l.serial_number) : ''}</div>
        </div>`).join('') + `</div></body></html>`);
      w.document.close();
    } catch (e) {
      toast.error('Failed to load labels');
    }
  };
  
  const handleAssignAsset = async (assetId, assignData) => {
    await apiCall(`/api/it-assets/${assetId}/assign`, {
      method: 'POST',
      body: JSON.stringify(assignData)
    });
    toast.success('Asset assigned successfully');
    fetchAssets();
    setShowAssignAsset(false);
    setSelectedAsset(null);
  };
  
  const handleReturnAsset = async (assetId, returnData) => {
    await apiCall(`/api/it-assets/${assetId}/return`, {
      method: 'POST',
      body: JSON.stringify(returnData)
    });
    toast.success('Asset returned successfully');
    fetchAssets();
  };
  
  const handleRetireAsset = (assetId) => setConfirmAction({
    title: 'Retire Asset?',
    message: 'The asset will be marked as retired and removed from the active inventory. This action cannot be undone.',
    confirmLabel: 'Retire Asset',
    run: async () => {
      await apiCall(`/api/it-assets/${assetId}`, { method: 'DELETE' });
      toast.success('Asset retired');
      fetchAssets();
    },
  });
  
  const viewAssetDetail = async (assetId) => {
    try {
      const asset = await apiCall(`/api/it-assets/${assetId}`);
      setSelectedAsset(asset);
      setShowAssetDetail(true);
    } catch (err) {
      toast.error('Failed to load asset details');
    }
  };
  
  // Fetch assets when tab changes to assets
  // IT-NAV-REDESIGN: load/refresh workspace tickets when entering IT Requests
  useEffect(() => {
    if (activeTab === 'requests') fetchTickets(ticketFilter, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // IT-PAGINATION-001: debounce search -> server query (full dataset), reset to page 1
  useEffect(() => {
    const t = setTimeout(() => setTicketQuery(ticketSearch.trim()), 350);
    return () => clearTimeout(t);
  }, [ticketSearch]);
  useEffect(() => {
    if (activeTab === 'requests') fetchTickets(ticketFilter, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketQuery, ticketLimit]);

  // Tasks tab: server-side pagination independent of dashboard widgets
  const fetchTaskList = async (page = taskPage, lim = taskLimit, statusFilter = taskStatusFilter) => {
    try {
      const data = await apiCall(`/api/it-dashboard/tasks?page=${page}&limit=${lim}&status=${statusFilter}`);
      setTaskList(data.tasks || []);
      setTaskTotal(data.total || 0);
      setTaskPage(page);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    }
  };
  useEffect(() => {
    if (activeTab === 'tasks') fetchTaskList(1, taskLimit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, taskLimit, taskStatusFilter]);

  useEffect(() => {
    if (activeTab === 'assets') {
      fetchAssets();
      fetchAllUsers();
      fetchOffboardingCases();
    }
  }, [activeTab, fetchAssets]);

  // HR-IT-OFFBOARD-001 — open offboarding cases triggered by HR exits
  const fetchOffboardingCases = async () => {
    try {
      const data = await apiCall('/api/it-offboarding/cases?status=open');
      setOffboardingCases(data.cases || []);
    } catch (err) {
      setOffboardingCases([]); // non-managers get 403 — strip simply hidden
    }
  };

  const acknowledgeOffboarding = async (caseId) => {
    try {
      await apiCall(`/api/it-offboarding/cases/${caseId}/acknowledge`, { method: 'POST' });
      toast.success('Offboarding acknowledged — case is now in progress');
      fetchOffboardingCases();
    } catch (err) { toast.error(err.message || 'Failed to acknowledge'); }
  };

  const clearOffboarding = async (caseId) => {
    try {
      const r = await apiCall(`/api/it-offboarding/cases/${caseId}/clear`, {
        method: 'POST', body: JSON.stringify({ force: false })
      });
      toast.success(`IT clearance completed — ${r?.assets_released?.length || 0} returned asset(s) unassigned; HR can now see this employee as cleared`);
      fetchOffboardingCases();
      fetchAssets();
    } catch (err) {
      // IT-OFFBOARD-FORCE-001: unconfirmed assets block normal clearance — review them in the case detail
      if (/not yet confirmed returned|Not Returned\/Lost/i.test(err.message || '')) setOffboardingDetailCaseId(caseId);
      toast.error(err.message || 'Cannot clear: assets still assigned');
    }
  };

  const startAssetRecovery = (c) => {
    const user = allUsers.find(u => u.id === c.user_id);
    if (!user) { toast.error('Linked user account not found for this employee'); return; }
    setOffboardInitialUser(user);
    setShowOffboardingModal(true);
  };
  
  // Ticket actions
  const handlePickupTicket = async (ticketId) => {
    try {
      await apiCall(`/api/it-dashboard/tickets/pickup?ticket_id=${ticketId}`, { method: 'POST' });
      fetchDashboardData();
      fetchTickets(ticketFilter, ticketPage);
    } catch (err) {
      toast.error(err.message || 'Failed to pick up ticket');
    }
  };
  
  const handleAssignTicket = async (ticketId, technicianId) => {
    try {
      await apiCall('/api/it-dashboard/tickets/assign', {
        method: 'POST',
        body: JSON.stringify({ ticket_id: ticketId, assigned_to: technicianId })
      });
      fetchDashboardData();
      fetchTickets(ticketFilter, ticketPage);
    } catch (err) {
      toast.error(err.message || 'Failed to assign ticket');
    }
  };

  // TICKET-UNASSIGN-001: return an assigned ticket to the unassigned queue
  // NO-NATIVE-POPUPS-001: TitleFlow modal with ticket + technician context
  const handleUnassignTicket = (ticket) => {
    const who = ticket.assigned_to_name || 'the current technician';
    const num = ticket.ticket_number || (ticket.id || '').slice(0, 8).toUpperCase();
    setConfirmAction({
      title: 'Unassign IT Request?',
      message: `This will remove ${who} from ticket ${num} and return the request to the Unassigned queue with status Open.`,
      confirmLabel: 'Unassign Ticket',
      run: async () => {
        await apiCall('/api/it-dashboard/tickets/unassign', {
          method: 'POST',
          body: JSON.stringify({ ticket_id: ticket.id })
        });
        toast.success(`Ticket ${num} returned to the Unassigned queue`);
        fetchDashboardData();
        fetchTickets(ticketFilter, ticketPage);
      },
    });
  };
  
  const handleUpdateTicketStatus = async (ticketId, status) => {
    try {
      await apiCall('/api/it-dashboard/tickets/status', {
        method: 'POST',
        body: JSON.stringify({ ticket_id: ticketId, status })
      });
      fetchDashboardData();
      fetchTickets(ticketFilter, ticketPage);
    } catch (err) {
      toast.error(err.message || 'Failed to update ticket status');
    }
  };
  
  // View ticket details with attachments
  const handleViewTicketDetail = async (ticketId) => {
    setLoadingTicketDetail(true);
    try {
      const ticketData = await apiCall(`/api/it-dashboard/tickets/${ticketId}`);
      setSelectedTicketDetail(ticketData);
      setShowTicketDetail(true);
    } catch (err) {
      toast.error(`Failed to load ticket details: ${err.message}`);
    } finally {
      setLoadingTicketDetail(false);
    }
  };
  
  // Download attachment
  const handleDownloadAttachment = async (ticketId, attachmentId, filename) => {
    try {
      const response = await apiCall(`/api/it-dashboard/tickets/${ticketId}/attachments/${attachmentId}/download`);
      
      if (response.download_url) {
        // Open download URL in new tab
        window.open(response.download_url, '_blank');
        // Jun 14, 2026 — FILES-TOAST-001: removed "Downloading …" toast.
      }
    } catch (err) {
      toast.error(`Failed to download: ${err.message}`);
    }
  };
  
  // Format date for display - V1 REQUIREMENT: Use ET timezone
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);
    
    if (dateOnly.getTime() === today.getTime()) return 'Today';
    if (dateOnly.getTime() === today.getTime() - 86400000) return 'Yesterday';
    if (dateOnly.getTime() === today.getTime() + 86400000) return 'Tomorrow';
    
    // Use ET timezone for dates beyond today/yesterday/tomorrow
    return formatDateShortET(dateStr);
  };
  
  // Format time for technician availability - V1 REQUIREMENT: Use ET timezone
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return formatTimeOnlyET(dateStr).replace(' ET', ''); // Remove ET suffix for cleaner time display
  };
  
  if (isLoading && !kpis) {
    return (
      <div className="it-dashboard loading" data-testid="it-dashboard-skeleton">
        <div className="itp-skeleton-wrap" aria-label="Loading IT Dashboard">
          <div className="itp-skel itp-skel-header" />
          <div className="itp-skel-row">
            {[...Array(7)].map((_, i) => <div key={i} className="itp-skel itp-skel-kpi" />)}
          </div>
          <div className="itp-skel itp-skel-panel" />
          <div className="itp-skel itp-skel-table" />
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="it-dashboard error">
        <AlertCircle size={48} />
        <h2>Error Loading Dashboard</h2>
        <p>{error}</p>
        <button onClick={fetchDashboardData} className="btn-primary">
          <RefreshCw size={16} /> Retry
        </button>
      </div>
    );
  }
  
  return (
    <div className="it-dashboard" data-testid="it-dashboard">
      {/* IT-UNIFIED-NAV: single sidebar — internal header removed; branding,
          session controls & notifications live in the AdminLayout header. */}
      {/* Reconnecting Banner - shown after 10s of disconnection */}
      {showReconnectingBanner && (
        <div className="reconnecting-banner">
          <RefreshCw size={14} className="spin" />
          <span>Reconnecting to real-time notifications...</span>
        </div>
      )}

      <div className="it-body">
        {/* IT-UNIFIED-NAV: THE single left navigation for the IT portal */}
        <ITSidebar activeTab={activeTab} onTabSelect={setActiveTab} isITManager={isITManager} />
      
      {/* Main Content */}
      <main className="it-main">
        {/* Status Bar */}
        <div className="dashboard-status-bar">
          {/* WebSocket Connection Status */}
          <div className={`ws-status ${wsConnected ? 'connected' : 'disconnected'}`} title={wsConnected ? 'Real-time notifications active' : 'Notifications disconnected'}>
            {wsConnected ? <Wifi size={14} /> : <WifiOff size={14} />}
            <span>{wsConnected ? 'Live' : 'Offline'}</span>
          </div>
          <button className="btn-refresh" onClick={fetchDashboardData} title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>
        
        {/* KPI Tiles and SLA Compliance — Dashboard tab only (module pages show their own KPIs) */}
        {activeTab === 'dashboard' && (
          <>
            {/* IT-DASH-PREMIUM — Welcome section */}
            <section className="itp-welcome itp-fade-up" data-testid="it-welcome-section">
              <div className="itp-welcome-text">
                <h1 data-testid="it-welcome-greeting">
                  {itGreeting(itNow)}, {user?.name || 'IT Manager'}! <span role="img" aria-label="wave">👋</span>
                </h1>
                <p>Here&rsquo;s what&rsquo;s happening across your IT operations.</p>
              </div>
              <div className="itp-clock-pill" data-testid="it-dashboard-clock">
                <Calendar size={14} />
                {/* eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional portal live clock */}
                <span>{itNow.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
                <span className="itp-clock-dot">·</span>
                {/* eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional portal live clock */}
                <span className="itp-clock-time">{itNow.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</span>
              </div>
            </section>

            {/* KPI Tiles */}
            <section className="kpi-section">
              <KPITile 
                value={kpis?.tasks_overdue || 0} 
                label="Tasks Overdue" 
                color="red"
                tier="critical"
                icon={AlertCircle}
                delay={60}
                testId="kpi-tasks-overdue"
                onClick={() => setActiveTab('tasks')}
              />
              <KPITile 
                value={kpis?.tasks_due_this_week || 0} 
                label="Tasks Due This Week" 
                color="teal"
                icon={Calendar}
                delay={120}
                testId="kpi-tasks-due-week"
                onClick={() => setActiveTab('tasks')}
              />
              <KPITile 
                value={kpis?.open_tickets || 0} 
                label="Open IT Requests" 
                color="green"
                icon={FileText}
                delay={180}
                testId="kpi-open-requests"
                onClick={() => handleFilterChange('open')}
              />
              <KPITile 
                value={kpis?.on_hold_tickets || 0} 
                label="On Hold Requests" 
                color="lime"
                icon={PauseCircle}
                delay={240}
                testId="kpi-on-hold"
                onClick={() => handleFilterChange('on_hold')}
              />
              <KPITile 
                value={kpis?.sla_breached || 0} 
                label="SLA Breached" 
                color="red"
                tier="critical"
                icon={AlertTriangle}
                delay={300}
                testId="kpi-sla-breached"
                onClick={() => handleFilterChange('breached')}
              />
              <KPITile 
                value={kpis?.unassigned_tickets || 0} 
                label="Unassigned Tickets" 
                color="indigo"
                tier="unassigned"
                icon={UserPlus}
                delay={360}
                testId="kpi-unassigned"
                onClick={() => handleFilterChange('unassigned')}
              />
              <KPITile 
                value={kpis?.sla_at_risk || 0} 
                label="SLA At Risk" 
                color="yellow"
                tier="warning"
                icon={Timer}
                delay={420}
                testId="kpi-sla-at-risk"
                onClick={() => handleFilterChange('at_risk')}
              />
            </section>
            
            {/* SLA Compliance Card */}
            {slaMetrics && (
              <SLAComplianceCard metrics={{...slaMetrics, total_tickets: slaMetrics.response?.met + slaMetrics.response?.breached}} />
            )}
          </>
        )}
        
        {/* Tab-based Content Rendering */}
        {activeTab === 'dashboard' && (
        <div className="dashboard-layout">
          {/* Compact Info Bar - Announcements & Reminders side by side */}
          <div className="info-bar">
            {/* Compact Announcements */}
            <section className="panel compact-announcements-panel">
              <div className="panel-header">
                <div className="panel-title-group">
                  <Megaphone size={16} />
                  <h3>Announcements</h3>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button className="itp-viewall" onClick={() => setActiveTab('announcements')} data-testid="announcements-view-all">
                    View all →
                  </button>
                  {canPostAnnouncements && (
                    <button className="btn-action" onClick={() => setShowAddAnnouncement(true)}>
                      <Plus size={14} /> Post
                    </button>
                  )}
                </div>
              </div>
              <div className="compact-announcements-list">
                {announcements.length === 0 ? (
                  <div className="empty-state-small">No announcements yet — post one to keep the team informed.</div>
                ) : (
                  announcements.slice(0, 2).map(announcement => (
                    <div key={announcement.id} className={`compact-announcement-item priority-${announcement.priority}`}>
                      <span className={`itp-ann-dot itp-ann-dot--${announcement.priority}`} aria-hidden="true" />
                      <span className="announcement-title">{announcement.title}</span>
                      <span className={`itp-badge itp-badge--${announcement.priority}`}>{announcement.priority}</span>
                      {announcement.created_at && (
                        <span className="itp-item-time">{formatDate(announcement.created_at)}</span>
                      )}
                      {isITManager && (
                        <button 
                          className="btn-delete-small"
                          onClick={() => handleDeleteAnnouncement(announcement.id)}
                          title="Delete"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))
                )}
                {announcements.length > 2 && (
                  <button className="btn-link" onClick={() => setActiveTab('announcements')}>
                    +{announcements.length - 2} more →
                  </button>
                )}
              </div>
            </section>
            
            {/* Compact Reminders */}
            <section className="panel compact-reminders-panel">
              <div className="panel-header">
                <div className="panel-title-group">
                  <Bell size={16} />
                  <h3>My Reminders</h3>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button className="itp-viewall" onClick={() => setActiveTab('reminders')} data-testid="reminders-view-all">
                    View all →
                  </button>
                  <button className="btn-action" onClick={() => setShowAddReminder(true)}>
                    <Plus size={14} /> Add
                  </button>
                </div>
              </div>
              <div className="compact-reminders-list">
                {reminders.length === 0 ? (
                  <div className="empty-state-small">No reminders — add one so nothing slips through.</div>
                ) : (
                  reminders.slice(0, 3).map(reminder => {
                    const isOverdue = new Date(reminder.due_date) < new Date() && !reminder.completed;
                    return (
                      <div key={reminder.id} className={`compact-reminder-item ${isOverdue ? 'overdue' : ''}`}>
                        <button 
                          className="reminder-check"
                          onClick={() => handleCompleteReminder(reminder.id)}
                          title="Mark complete"
                        >
                          <CheckCircle size={14} />
                        </button>
                        <span className="reminder-title">{reminder.title}</span>
                        {reminder.due_date && (
                          <span className="itp-item-time">{formatDate(reminder.due_date)}</span>
                        )}
                        <span className={`reminder-priority priority-${reminder.priority}`}>
                          {reminder.priority}
                        </span>
                      </div>
                    );
                  })
                )}
                {reminders.length > 3 && (
                  <button className="btn-link" onClick={() => setActiveTab('reminders')}>
                    +{reminders.length - 3} more →
                  </button>
                )}
              </div>
            </section>
            
            {/* Compact Quick Stats + System Health (real /api/health data only) */}
            <section className="panel compact-stats-panel">
              <div className="quick-stat" data-testid="quick-stat-tasks-due">
                <span className="stat-label">Tasks Due</span>
                <span className="stat-value">{tasks.filter(t => !t.completed).length}</span>
                <button className="btn-link" onClick={() => setActiveTab('tasks')}>View →</button>
              </div>
              <div className="quick-stat" data-testid="quick-stat-online-techs">
                <span className="stat-label">Online Techs</span>
                <span className="stat-value">{technicians.filter(t => t.status === 'online').length}</span>
              </div>
              <div className="itp-health" data-testid="system-health-card">
                <div className="itp-health-head">
                  <span className="stat-label">System Health</span>
                  {apiHealth ? (
                    <span className={`itp-health-pill ${apiHealth.status === 'healthy' ? 'ok' : 'warn'}`}>
                      {apiHealth.status === 'healthy' ? 'Operational' : apiHealth.status}
                    </span>
                  ) : (
                    <span className="itp-health-pill unknown">Unavailable</span>
                  )}
                </div>
                {apiHealth ? (
                  <>
                    <div className="itp-health-row">
                      <span className={`itp-health-dot ${apiHealth.status === 'healthy' ? 'ok' : 'warn'}`} aria-hidden="true" />
                      <span>API Service</span>
                    </div>
                    {apiHealth.timestamp && (
                      <span className="itp-health-checked">Checked {formatTime(apiHealth.timestamp)} ET</span>
                    )}
                  </>
                ) : (
                  <p className="itp-health-empty">No live health data available right now.</p>
                )}
              </div>
            </section>
          </div>
          
          {/* IT-NAV-REDESIGN: compact Recent Requests preview — full workspace lives in IT Requests */}
          <section className="panel requests-panel full-width" data-testid="recent-requests-panel">
            <div className="panel-header">
              <h3>Recent Requests</h3>
              <div className="panel-actions">
                <button
                  className="btn-link open-requests-link"
                  onClick={() => setActiveTab('requests')}
                  data-testid="open-it-requests-link"
                >
                  Open IT Requests →
                </button>
              </div>
            </div>
            <div className="table-container full-width-table">
              <table className="data-table tickets-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Subject</th>
                    <th>Requester</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>SLA</th>
                    <th>Technician</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTickets.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="empty-state">No recent requests</td>
                    </tr>
                  ) : (
                    recentTickets.slice(0, 5).map(ticket => (
                      <tr
                        key={ticket.id}
                        className={`recent-request-row ${ticket.sla?.overall_status === 'breached' ? 'row-breached' : ticket.sla?.overall_status === 'at_risk' ? 'row-at-risk' : ''}`}
                        onClick={() => handleViewTicketDetail(ticket.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleViewTicketDetail(ticket.id); }}
                        role="button"
                        tabIndex={0}
                        data-testid={`recent-request-row-${ticket.id}`}
                      >
                        <td className="ticket-id">{ticket.ticket_number?.split('-').pop() || ticket.id.slice(0, 4).toUpperCase()}</td>
                        <td className="ticket-subject" style={{ color: '#fff' }}>{ticket.title}</td>
                        <td>{ticket.created_by_name || '—'}</td>
                        <td><StatusBadge status={ticket.status} /></td>
                        <td><PriorityBadge priority={ticket.priority} /></td>
                        <td><SLABadge sla={ticket.sla} /></td>
                        <td className="technician-cell">{ticket.assigned_to_name || <span className="no-files">Unassigned</span>}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
        )}

        {/* IT-NAV-REDESIGN: dedicated IT Requests ticket-management workspace */}
        {activeTab === 'requests' && (
        <div className="tab-content requests-tab" data-testid="requests-workspace">
          <section className="panel requests-panel full-width">
            <div className="panel-header requests-toolbar">
              <h3><Ticket size={16} /> IT Requests</h3>
              <div className="panel-actions">
                <div className="req-search" data-testid="requests-search">
                  <Search size={14} />
                  <input
                    type="text"
                    value={ticketSearch}
                    onChange={(e) => setTicketSearch(e.target.value)}
                    placeholder="Search ID, subject or requester..."
                    data-testid="requests-search-input"
                  />
                  {ticketSearch && (
                    <button className="req-search-clear" onClick={() => setTicketSearch('')} title="Clear search">
                      <X size={12} />
                    </button>
                  )}
                </div>
                <button className="btn-action" onClick={() => setShowAddIncident(true)} data-testid="requests-new-incident-btn">
                  <Plus size={14} /> New Incident
                </button>
                <button 
                  className="btn-action"
                  onClick={() => {
                    const ticket = tickets.find(t => !t.assigned_to);
                    if (ticket) handlePickupTicket(ticket.id);
                  }}
                  disabled={!tickets.some(t => !t.assigned_to)}
                  title="Pick up the next unassigned request"
                  data-testid="requests-pickup-btn"
                >
                  Pick Up
                </button>
              </div>
            </div>
            
            {/* Filter Tabs */}
            <div className="filter-tabs">
              <button 
                className={ticketFilter === 'all' ? 'active' : ''}
                onClick={() => handleFilterChange('all')}
                data-testid="requests-filter-all"
              >
                All
              </button>
              <button 
                className={ticketFilter === 'open' ? 'active' : ''}
                onClick={() => handleFilterChange('open')}
                data-testid="requests-filter-open"
              >
                Open
              </button>
              <button 
                className={ticketFilter === 'in_progress' ? 'active' : ''}
                onClick={() => handleFilterChange('in_progress')}
                data-testid="requests-filter-in-progress"
              >
                In Progress
              </button>
              <button 
                className={ticketFilter === 'on_hold' ? 'active' : ''}
                onClick={() => handleFilterChange('on_hold')}
                data-testid="requests-filter-on-hold"
              >
                On Hold
              </button>
              <button 
                className={ticketFilter === 'unassigned' ? 'active' : ''}
                onClick={() => handleFilterChange('unassigned')}
                data-testid="requests-filter-unassigned"
              >
                Unassigned
              </button>
              <span className="filter-divider">|</span>
              <button 
                className={`filter-sla ${ticketFilter === 'breached' ? 'active sla-breached' : ''}`}
                onClick={() => handleFilterChange('breached')}
                data-testid="requests-filter-breached"
              >
                <AlertTriangle size={12} /> Breached
              </button>
              <button 
                className={`filter-sla ${ticketFilter === 'at_risk' ? 'active sla-at-risk' : ''}`}
                onClick={() => handleFilterChange('at_risk')}
                data-testid="requests-filter-at-risk"
              >
                <AlertCircle size={12} /> At Risk
              </button>
            </div>
            
            <div className="table-container full-width-table">
              <table className="data-table tickets-table" data-testid="requests-table">
                <thead>
                  <tr>
                    <th>Ticket ID</th>
                    <th>Subject</th>
                    <th>Requester</th>
                    <th>Technician</th>
                    <th>Category</th>
                    <th>Priority</th>
                    <th>SLA</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="empty-state">{ticketQuery ? `No requests match "${ticketQuery}"` : 'No tickets found'}</td>
                    </tr>
                  ) : (
                    tickets.map(ticket => (
                      <tr key={ticket.id} className={ticket.sla?.overall_status === 'breached' ? 'row-breached' : ticket.sla?.overall_status === 'at_risk' ? 'row-at-risk' : ''}>
                        <td className="ticket-id">{ticket.ticket_number || ticket.id.slice(0, 8).toUpperCase()}</td>
                        <td className="ticket-subject" style={{ color: '#fff' }}>
                          {ticket.title}
                          {ticket.attachments?.filter(a => a.upload_status === 'completed').length > 0 && (
                            <button 
                              className="btn-attachments inline"
                              onClick={() => handleViewTicketDetail(ticket.id)}
                              title={`${ticket.attachments.filter(a => a.upload_status === 'completed').length} file(s) attached`}
                            >
                              <Paperclip size={12} />
                              <span>{ticket.attachments.filter(a => a.upload_status === 'completed').length}</span>
                            </button>
                          )}
                        </td>
                        <td>{ticket.created_by_name || '—'}</td>
                        <td className="technician-cell">
                          {ticket.assigned_to_name || (
                            <button 
                              className="btn-assign"
                              onClick={() => handlePickupTicket(ticket.id)}
                            >
                              Assign
                            </button>
                          )}
                        </td>
                        <td className="category-cell">{ticket.category ? ticket.category.replace(/_/g, ' ') : '—'}</td>
                        <td><PriorityBadge priority={ticket.priority} /></td>
                        <td><SLABadge sla={ticket.sla} /></td>
                        <td><StatusBadge status={ticket.status} /></td>
                        <td>
                          <div className="action-buttons">
                            {!ticket.assigned_to && (
                              <button 
                                className="btn-small"
                                onClick={() => handlePickupTicket(ticket.id)}
                                title="Pick Up"
                                data-testid={`ticket-pickup-${ticket.id}`}
                              >
                                <UserPlus size={14} />
                              </button>
                            )}
                            {ticket.assigned_to && isITManager && (
                              <button 
                                className="btn-small"
                                onClick={() => handleUnassignTicket(ticket)}
                                title="Unassign"
                                data-testid={`ticket-unassign-${ticket.id}`}
                              >
                                <UserMinus size={14} />
                              </button>
                            )}
                            {ticket.status !== 'on_hold' && ticket.status !== 'resolved' && (
                              <button 
                                className="btn-small"
                                onClick={() => handleUpdateTicketStatus(ticket.id, 'on_hold')}
                                title="Put On Hold"
                                data-testid={`ticket-hold-${ticket.id}`}
                              >
                                <PauseCircle size={14} />
                              </button>
                            )}
                            {ticket.status === 'on_hold' && (
                              <button 
                                className="btn-small"
                                onClick={() => handleUpdateTicketStatus(ticket.id, 'in_progress')}
                                title="Resume"
                                data-testid={`ticket-resume-${ticket.id}`}
                              >
                                <RefreshCw size={14} />
                              </button>
                            )}
                            {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                              <button 
                                className="btn-small btn-success"
                                onClick={() => handleUpdateTicketStatus(ticket.id, 'resolved')}
                                title="Resolve"
                                data-testid={`ticket-resolve-${ticket.id}`}
                              >
                                <CheckCircle size={14} />
                              </button>
                            )}
                            <button 
                              className="btn-small btn-view"
                              onClick={() => handleViewTicketDetail(ticket.id)}
                              title="View Details"
                              data-testid={`ticket-view-${ticket.id}`}
                            >
                              <Eye size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              
              {/* IT-PAGINATION-001 */}
              <ITPagination
                page={ticketPage}
                limit={ticketLimit}
                total={ticketTotal}
                onPageChange={(p) => fetchTickets(ticketFilter, p)}
                onLimitChange={(l) => setTicketLimit(l)}
                testid="requests-pagination"
              />
            </div>
          </section>
        </div>
        )}
        
        {/* Tasks Tab View */}
        {activeTab === 'tasks' && (
          <div className="tab-content tasks-tab">
            <section className="panel full-panel">
              <div className="panel-header">
                <h3>IT Tasks</h3>
                <div className="panel-actions">
                  <button className="btn-action" onClick={() => setShowAddTask(true)}>
                    <Plus size={14} /> Add Task
                  </button>
                </div>
              </div>
              <div className="filter-tabs" data-testid="task-status-tabs">
                {[
                  ['all', 'All Tasks'],
                  ['open', 'Open'],
                  ['in_progress', 'In Progress'],
                  ['completed', 'Completed'],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    className={taskStatusFilter === key ? 'active' : ''}
                    onClick={() => setTaskStatusFilter(key)}
                    data-testid={`task-tab-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Task</th>
                      <th>Assigned To</th>
                      <th>Due Date</th>
                      <th>Priority</th>
                      <th>Status</th>
                      {taskStatusFilter === 'completed' && <th>Completed</th>}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taskList.length === 0 ? (
                      <tr><td colSpan={taskStatusFilter === 'completed' ? 7 : 6} className="empty-state">No tasks found</td></tr>
                    ) : (
                      taskList.map(task => (
                        <tr key={task.id}>
                          <td className="task-title">{task.title}</td>
                          <td>{task.assigned_to_name || 'Unassigned'}</td>
                          <td>{formatDate(task.due_date)}</td>
                          <td><PriorityBadge priority={task.priority} /></td>
                          <td><StatusBadge status={task.status} /></td>
                          {taskStatusFilter === 'completed' && (
                            <td style={{ fontSize: 12.5 }}>
                              {task.completed_at ? formatDate(task.completed_at) : '—'}
                              {task.completed_by_name ? <span style={{ color: '#64748b' }}> · {task.completed_by_name}</span> : null}
                            </td>
                          )}
                          <td>
                            <button className="btn-small" onClick={() => setTaskDetailTarget(task)} title="View Details" data-testid={`task-view-${task.id}`}>
                              <Eye size={14} />
                            </button>
                            {task.status !== 'completed' && task.status !== 'cancelled' && (
                              <button className="btn-small" onClick={() => setCompleteTaskTarget(task)} title="Complete" data-testid={`task-complete-${task.id}`}>
                                <CheckCircle size={14} />
                              </button>
                            )}
                            <button className="btn-small btn-danger" onClick={() => handleDeleteTask(task.id)} title="Delete">
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                <ITPagination
                  page={taskPage}
                  limit={taskLimit}
                  total={taskTotal}
                  onPageChange={(p) => fetchTaskList(p)}
                  onLimitChange={(l) => setTaskLimit(l)}
                  testid="tasks-pagination"
                />
              </div>
            </section>
          </div>
        )}
        
        {/* Reminders Tab View */}
        {activeTab === 'reminders' && (
          <div className="tab-content reminders-tab">
            <section className="panel full-panel">
              <div className="panel-header">
                <div className="panel-title-group">
                  <Bell size={18} />
                  <h3>My Reminders</h3>
                </div>
                <button className="btn-action" onClick={() => setShowAddReminder(true)}>
                  <Plus size={14} /> Add Reminder
                </button>
              </div>
              <div className="reminders-full-list">
                {reminders.length === 0 ? (
                  <div className="empty-state-large">
                    <Bell size={48} />
                    <h4>No Reminders</h4>
                    <p>You don&apos;t have any reminders yet. Add one to stay on track!</p>
                    <button className="btn-primary" onClick={() => setShowAddReminder(true)}>
                      <Plus size={16} /> Add Your First Reminder
                    </button>
                  </div>
                ) : (
                  reminders.map(reminder => {
                    const isOverdue = new Date(reminder.due_date) < new Date() && !reminder.completed;
                    const isDueToday = new Date(reminder.due_date).toDateString() === new Date().toDateString();
                    return (
                      <div key={reminder.id} className={`reminder-item-large ${isOverdue ? 'overdue' : ''} ${isDueToday ? 'due-today' : ''}`}>
                        <button className="reminder-check" onClick={() => handleCompleteReminder(reminder.id)} title="Complete">
                          <CheckCircle size={20} />
                        </button>
                        <div className="reminder-content">
                          <span className="reminder-title">{reminder.title}</span>
                          {reminder.description && <p className="reminder-desc">{reminder.description}</p>}
                          <span className="reminder-due">
                            {isOverdue ? '⚠️ Overdue: ' : isDueToday ? '📅 Due Today' : '📅 Due: '}
                            {formatDate(reminder.due_date)}
                          </span>
                        </div>
                        <span className={`reminder-priority priority-${reminder.priority}`}>{reminder.priority}</span>
                        <button className="btn-delete-small" onClick={() => handleDeleteReminder(reminder.id)} title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </div>
        )}
        
        {/* Announcements Tab View */}
        {activeTab === 'announcements' && (
          <div className="tab-content announcements-tab">
            <section className="panel full-panel">
              <div className="panel-header">
                <div className="panel-title-group">
                  <Megaphone size={18} />
                  <h3>IT Announcements</h3>
                </div>
                {canPostAnnouncements && (
                  <button className="btn-action" data-testid="post-announcement-btn" onClick={() => setShowAddAnnouncement(true)}>
                    <Plus size={14} /> Post Announcement
                  </button>
                )}
              </div>
              <div className="announcements-full-list">
                {announcements.length === 0 ? (
                  <div className="empty-state-large">
                    <Megaphone size={48} />
                    <h4>No Announcements</h4>
                    <p>There are no announcements for the IT team at this time.</p>
                    {canPostAnnouncements && (
                      <button className="btn-primary" onClick={() => setShowAddAnnouncement(true)}>
                        <Plus size={16} /> Post First Announcement
                      </button>
                    )}
                  </div>
                ) : (
                  announcements.map(announcement => (
                    <div key={announcement.id} className={`announcement-item-large priority-${announcement.priority}`}>
                      <div className="announcement-header">
                        <span className={`announcement-priority-badge ${announcement.priority}`}>
                          {announcement.priority === 'critical' ? '🔴 Critical' : 
                           announcement.priority === 'important' ? '🟠 Important' : '🔵 Normal'}
                        </span>
                        <span
                          data-testid={`announcement-audience-${announcement.id}`}
                          style={{
                            fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '2px 10px',
                            background: announcement.audience === 'all' ? '#dbeafe' : '#f1f5f9',
                            color: announcement.audience === 'all' ? '#1d4ed8' : '#475569',
                          }}
                        >
                          {announcement.audience === 'all' ? 'All Staff' : 'IT Team'}
                        </span>
                        <span className="announcement-date">{formatDate(announcement.created_at)}</span>
                        {isITManager && (
                          <button
                            className="btn-action"
                            data-testid={`announcement-reads-btn-${announcement.id}`}
                            style={{ fontSize: 12, padding: '3px 10px' }}
                            onClick={() => setReadsAnnouncementId(announcement.id)}
                            title="Who has read this?"
                          >
                            <Eye size={13} /> {announcement.read_count ?? 0} read
                          </button>
                        )}
                        {isITManager && (
                          <button className="btn-delete-small" data-testid={`announcement-delete-btn-${announcement.id}`} onClick={() => handleDeleteAnnouncement(announcement.id)}>
                            <X size={14} />
                          </button>
                        )}
                      </div>
                      <h4 className="announcement-title">{announcement.title}</h4>
                      <p className="announcement-content">{announcement.content}</p>
                      <div className="announcement-footer">
                        <span>Posted by {announcement.created_by_name}</span>
                        {announcement.expires_at && (
                          <span className="expires-info">• Expires: {formatDate(announcement.expires_at)}</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        )}
        
        {/* Assets Tab View */}
        {activeTab === 'assets' && (
          <div className="tab-content assets-tab">
            {/* Module header */}
            <div className="itm-header" data-testid="assets-module-header">
              <div className="itm-header-icon"><Package size={19} /></div>
              <div>
                <h2>Asset Management</h2>
                <p>IT asset inventory, assignments and lifecycle</p>
              </div>
            </div>
            {/* HR-IT-OFFBOARD-001 — cases auto-created when HR marks an exit */}
            {offboardingCases.length > 0 && (
              <div className="offb-strip" data-testid="offboarding-cases-strip">
                <div className="offb-strip-title">
                  <UserCheck size={14} /> Employee Offboarding Required
                  <span className="offb-count">{offboardingCases.length}</span>
                </div>
                {offboardingCases.map(c => {
                  const lwd = c.last_working_day;
                  const daysLeft = lwd ? Math.ceil((new Date(lwd) - new Date()) / 86400000) : null;
                  return (
                    <div key={c.id} className="offb-case" data-testid={`offboarding-case-${c.employee_id}`}>
                      <div className="offb-case-main">
                        <span className="offb-name">{c.employee_name}</span>
                        <span className={`offb-trigger offb-${c.trigger_status}`}>{c.trigger_status}</span>
                        <span className={`offb-status offb-st-${c.clearance_status}`}>
                          {{ pending: 'Pending', in_progress: 'In Progress', awaiting_asset_return: 'Awaiting Asset Return', escalated_to_hr: 'Escalated to HR' }[c.clearance_status] || c.clearance_status}
                        </span>
                      </div>
                      <div className="offb-case-meta">
                        <span>
                          LWD: <strong>{lwd ? formatDateOnly(lwd) : 'not set'}</strong>
                          {daysLeft !== null && (
                            <em className={daysLeft < 0 ? 'offb-overdue' : ''}>
                              {daysLeft < 0 ? ` · ${Math.abs(daysLeft)}d overdue` : daysLeft === 0 ? ' · today' : ` · in ${daysLeft}d`}
                            </em>
                          )}
                        </span>
                        <span>Assets remaining: <strong>{c.assets_remaining}</strong></span>
                      </div>
                      <div className="offb-case-actions">
                        {c.clearance_status === 'pending' && (
                          <button className="offb-btn" onClick={() => acknowledgeOffboarding(c.id)}
                            data-testid={`offb-ack-${c.employee_id}`}>Acknowledge</button>
                        )}
                        {c.user_id && c.assets_remaining > 0 && (
                          <button className="offb-btn offb-btn-primary" onClick={() => startAssetRecovery(c)}
                            data-testid={`offb-recover-${c.employee_id}`}>Start IT Offboarding</button>
                        )}
                        <button className="offb-btn" onClick={() => setOffboardingDetailCaseId(c.id)}
                          data-testid={`offb-details-${c.employee_id}`}>Details</button>
                        <button className="offb-btn offb-btn-clear" onClick={() => clearOffboarding(c.id)}
                          data-testid={`offb-clear-${c.employee_id}`}>Mark Cleared</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Asset Stats — clickable KPI shortcuts that filter the inventory below */}
            {assetStats && (
              <div className="asset-stats-grid">
                <button
                  className={`asset-stat-card stat-clickable ${!assetWarranty && assetFilter === 'all' ? 'stat-active' : ''}`}
                  onClick={() => selectAssetKpi('all')}
                  data-testid="asset-kpi-total"
                >
                  <Package size={20} />
                  <div className="stat-info">
                    <span className="stat-value" data-testid="assets-total-count">{assetStats.total}</span>
                    <span className="stat-label">Total Assets</span>
                  </div>
                </button>
                <button
                  className={`asset-stat-card available stat-clickable ${!assetWarranty && assetFilter === 'available' ? 'stat-active' : ''}`}
                  onClick={() => selectAssetKpi('available')}
                  data-testid="asset-kpi-available"
                >
                  <CheckCircle size={20} />
                  <div className="stat-info">
                    <span className="stat-value">{assetStats.available}</span>
                    <span className="stat-label">Available</span>
                  </div>
                </button>
                <button
                  className={`asset-stat-card assigned stat-clickable ${!assetWarranty && assetFilter === 'assigned' ? 'stat-active' : ''}`}
                  onClick={() => selectAssetKpi('assigned')}
                  data-testid="asset-kpi-assigned"
                >
                  <UserCheck size={20} />
                  <div className="stat-info">
                    <span className="stat-value">{assetStats.assigned}</span>
                    <span className="stat-label">Assigned</span>
                  </div>
                </button>
                <button
                  className={`asset-stat-card maintenance stat-clickable ${!assetWarranty && assetFilter === 'maintenance' ? 'stat-active' : ''}`}
                  onClick={() => selectAssetKpi('maintenance')}
                  data-testid="asset-kpi-maintenance"
                >
                  <Wrench size={20} />
                  <div className="stat-info">
                    <span className="stat-value">{assetStats.maintenance}</span>
                    <span className="stat-label">In Maintenance</span>
                  </div>
                </button>
                <button
                  className={`asset-stat-card pending stat-clickable ${!assetWarranty && assetFilter === 'pending_return' ? 'stat-active' : ''}`}
                  onClick={() => selectAssetKpi('pending_return')}
                  data-testid="asset-kpi-pending-return"
                >
                  <Clock size={20} />
                  <div className="stat-info">
                    <span className="stat-value">{assetStats.pending_return}</span>
                    <span className="stat-label">Pending Return</span>
                  </div>
                </button>
                <button
                  className={`asset-stat-card warning stat-clickable ${assetWarranty ? 'stat-active' : ''}`}
                  onClick={selectWarrantyKpi}
                  data-testid="asset-kpi-warranty"
                >
                  <AlertTriangle size={20} />
                  <div className="stat-info">
                    <span className="stat-value">{assetStats.warranty_expiring_soon}</span>
                    <span className="stat-label">Warranty Expiring</span>
                  </div>
                </button>
              </div>
            )}
            
            {/* Quick Actions Row */}
            <div className="asset-quick-actions">
              <button 
                className="btn-action btn-qr-scan"
                onClick={() => setShowQRScanner(true)}
              >
                <Camera size={16} /> Scan QR Code
              </button>
              {isITManager && (
                <button 
                  className="btn-action btn-offboarding"
                  onClick={() => setShowOffboardingModal(true)}
                >
                  <UserMinus size={16} /> Employee Offboarding
                </button>
              )}
              {isITManager && (
                <button 
                  className="btn-action"
                  onClick={() => setShowProductTypes(true)}
                  data-testid="manage-product-types-btn"
                >
                  <Layers size={16} /> Product Types
                </button>
              )}
              <button 
                className="btn-action"
                onClick={handlePrintLabels}
                data-testid="print-qr-labels-btn"
              >
                <Printer size={16} /> Print QR Labels
              </button>
            </div>
            
            {/* Assets Panel */}
            <section className="panel full-panel assets-panel">
              <div className="panel-header">
                <div className="panel-title-group">
                  <Package size={18} />
                  <h3>IT Asset Inventory</h3>
                </div>
                <div className="panel-actions">
                  <div className="asset-view-toggle" role="tablist" data-testid="asset-view-toggle">
                    <button type="button" role="tab" className={assetView === 'assets' ? 'active' : ''}
                      onClick={() => setAssetView('assets')} aria-selected={assetView === 'assets'} data-testid="asset-view-assets">
                      <ListIcon size={14} /> Assets
                    </button>
                    <button type="button" role="tab" className={assetView === 'employees' ? 'active' : ''}
                      onClick={() => setAssetView('employees')} aria-selected={assetView === 'employees'} data-testid="asset-view-employees">
                      <Users size={14} /> By Employee
                      {assetView === 'employees' && employeeTotals.employees > 0 && <span className="avt-count">{employeeTotals.employees}</span>}
                    </button>
                  </div>
                  <div className="asset-filters">
                    <select 
                      value={assetWarranty ? 'all' : assetFilter} 
                      onChange={e => { setAssetWarranty(null); setAssetFilter(e.target.value); }}
                      className="filter-select"
                      data-testid="assets-status-filter"
                    >
                      <option value="all">All Status</option>
                      <option value="available">Available</option>
                      <option value="assigned">Assigned</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="pending_return">Pending Return</option>
                      <option value="retired">Retired</option>
                    </select>
                    <div className="search-box">
                      <Search size={14} />
                      <input 
                        type="text" 
                        placeholder="Search assets..." 
                        value={assetSearch}
                        onChange={e => setAssetSearch(e.target.value)}
                        data-testid="assets-search-input"
                      />
                    </div>
                  </div>
                  {isITManager && (
                    <button className="btn-export-report" onClick={handleExportAssets} disabled={exportingAssets}
                      title="Export the current inventory (respects status, warranty and search filters)" data-testid="export-assets-btn">
                      <FileSpreadsheet size={14} /> {exportingAssets ? 'Exporting…' : 'Export Report'}
                    </button>
                  )}
                  {isITManager && (
                    <button className="btn-primary" onClick={() => setShowAddAsset(true)} data-testid="add-asset-btn">
                      <Plus size={14} /> Add Asset
                    </button>
                  )}
                </div>
              </div>

              {/* Active filter indicator + warranty sub-filters */}
              {(assetWarranty || assetFilter !== 'all') && (
                <div className="asset-active-filter" data-testid="asset-active-filter">
                  <Filter size={13} />
                  {assetWarranty ? (
                    <>
                      <span className="aaf-label">Warranty:</span>
                      {[['30', '< 30 Days'], ['60', '< 60 Days'], ['90', '< 90 Days'], ['expired', 'Expired']].map(([v, l]) => (
                        <button
                          key={v}
                          className={`aaf-chip ${assetWarranty === v ? 'active' : ''}`}
                          onClick={() => setAssetWarranty(v)}
                          data-testid={`warranty-chip-${v}`}
                        >
                          {l}
                        </button>
                      ))}
                    </>
                  ) : (
                    <span className="aaf-label">Status: <strong>{assetFilter.replace('_', ' ')}</strong></span>
                  )}
                  <span className="aaf-count">{assetPageInfo.total} asset{assetPageInfo.total === 1 ? '' : 's'}</span>
                  <button
                    className="aaf-clear"
                    onClick={() => { setAssetWarranty(null); setAssetFilter('all'); }}
                    data-testid="asset-filter-clear"
                  >
                    <X size={12} /> Clear
                  </button>
                </div>
              )}

              {assetView === 'employees' ? (
                <AssetsByEmployee
                  groups={employeeGroups}
                  loading={employeeGroupsLoading && employeeGroups.length === 0}
                  expandedId={expandedEmployee}
                  onToggle={(id) => setExpandedEmployee(prev => (prev === id ? null : id))}
                  renderActions={renderAssetActions}
                  search={assetQuery.trim()}
                />
              ) : (
              <div className="table-container">
                <table className="data-table assets-table" data-testid="assets-table">
                  <thead>
                    <tr>
                      <th>Asset Tag</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Device No.</th>
                      <th>Stock State</th>
                      <th>Risk</th>
                      <th>Status</th>
                      <th>Assigned To</th>
                      <th>Condition</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.length === 0 ? (
                      <tr><td colSpan={10} className="empty-state">No assets found</td></tr>
                    ) : (
                      assets.map(asset => (
                        <tr key={asset.id} className={asset.status === 'retired' ? 'row-retired' : ''}>
                          <td className="asset-tag-cell">
                            <code>{asset.asset_tag}</code>
                          </td>
                          <td className="asset-name">{asset.name}</td>
                          <td>
                            <span className={`category-badge cat-${asset.category}`}>
                              {asset.asset_type_name || asset.category}
                            </span>
                          </td>
                          <td className="text-xs" data-testid={`asset-device-number-${asset.id}`}>{asset.legacy_device_number || '-'}</td>
                          <td>
                            <span className={`status-badge stock-${asset.stock_state || 'unspecified'}`} data-testid={`asset-stock-state-${asset.id}`}>
                              {asset.stock_state === 'working' ? 'Working' : asset.stock_state === 'non_working' ? 'Non-Working' : 'Unspecified'}
                            </span>
                          </td>
                          <td>
                            {asset.risk ? (
                              <span className={`status-badge risk-${asset.risk}`} data-testid={`asset-risk-${asset.id}`}>
                                {asset.risk}
                              </span>
                            ) : '-'}
                          </td>
                          <td>
                            <span className={`status-badge status-${asset.status}`}>
                              {asset.status?.replace('_', ' ')}
                            </span>
                          </td>
                          <td>{asset.assigned_to_name || '-'}</td>
                          <td>
                            <span className={`condition-badge cond-${asset.condition}`}>
                              {asset.condition}
                            </span>
                          </td>
                          <td className="actions-cell">{renderAssetActions(asset)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {assetPageInfo.total > 0 && (
                  <ITPagination
                    page={assetPage}
                    limit={assetLimit}
                    total={assetPageInfo.total}
                    onPageChange={(p) => setAssetPage(p)}
                    onLimitChange={(l) => { setAssetLimit(l); setAssetPage(1); }}
                    testid="assets-pagination"
                  />
                )}
              </div>
              )}
            </section>
          </div>
        )}
        
        {/* Reports Tab View */}
        {activeTab === 'reports' && isITManager && (
          <div className="tab-content reports-tab">
            <ReportsPanel isITManager={isITManager} />
          </div>
        )}
        
        {/* Audit Log Tab View */}
        {activeTab === 'audit' && isITManager && (
          <div className="tab-content audit-tab">
            <AuditLogPanel />
          </div>
        )}
        
        {/* Credentials Tab View - Secrets Vault */}
        {activeTab === 'credentials' && (
          <div className="tab-content credentials-tab">
            <CredentialsPanel />
          </div>
        )}
        
        {/* Downtime Analytics Tab View - Available to all IT roles */}
        {activeTab === 'downtime' && (
          <div className="tab-content downtime-tab">
            <DowntimeAnalytics />
          </div>
        )}
      </main>
      </div>
      
      {/* Modals */}
      {offboardingDetailCaseId && (
        <OffboardingCaseModal
          caseId={offboardingDetailCaseId}
          onClose={() => setOffboardingDetailCaseId(null)}
          onChanged={() => { fetchOffboardingCases(); fetchAssets(); }}
        />
      )}
      <CompleteTaskModal
        isOpen={!!completeTaskTarget}
        onClose={() => setCompleteTaskTarget(null)}
        task={completeTaskTarget}
        onSubmit={(notes) => handleCompleteTask(completeTaskTarget.id, notes)}
      />
      
      <TaskDetailModal
        isOpen={!!taskDetailTarget}
        onClose={() => setTaskDetailTarget(null)}
        task={taskDetailTarget}
      />
      
      <AddTaskModal 
        isOpen={showAddTask}
        onClose={() => setShowAddTask(false)}
        onSubmit={handleCreateTask}
        technicians={technicians}
      />
      
      <AddIncidentModal
        isOpen={showAddIncident}
        onClose={() => setShowAddIncident(false)}
        onSubmit={handleCreateIncident}
        technicians={technicians}
      />
      
      <AddAnnouncementModal
        isOpen={showAddAnnouncement}
        onClose={() => setShowAddAnnouncement(false)}
        onSubmit={handleCreateAnnouncement}
      />
      
      <AddReminderModal
        isOpen={showAddReminder}
        onClose={() => setShowAddReminder(false)}
        onSubmit={handleCreateReminder}
      />
      
      <TicketDetailModal
        isOpen={showTicketDetail}
        onClose={() => {
          setShowTicketDetail(false);
          setSelectedTicketDetail(null);
        }}
        ticket={selectedTicketDetail}
        onDownloadAttachment={handleDownloadAttachment}
        onPickup={handlePickupTicket}
        onUpdateStatus={handleUpdateTicketStatus}
      />
      
      {/* Asset Management Modals */}
      <AddAssetModal
        isOpen={showAddAsset}
        onClose={() => setShowAddAsset(false)}
        onSubmit={handleCreateAsset}
        assetTypes={assetTypes}
      />
      
      <ProductTypesModal
        isOpen={showProductTypes}
        onClose={() => setShowProductTypes(false)}
        apiCall={apiCall}
        onChanged={fetchAssets}
      />
      
      <EditAssetModal
        isOpen={!!editingAsset}
        onClose={() => setEditingAsset(null)}
        onSubmit={handleUpdateAsset}
        asset={editingAsset}
        assetTypes={assetTypes}
      />
      
      <AssignAssetModal
        isOpen={showAssignAsset}
        onClose={() => {
          setShowAssignAsset(false);
          setSelectedAsset(null);
        }}
        onSubmit={handleAssignAsset}
        asset={selectedAsset}
        users={allUsers}
      />
      
      <AssetDetailModal
        isOpen={showAssetDetail}
        onClose={() => {
          setShowAssetDetail(false);
          setSelectedAsset(null);
        }}
        asset={selectedAsset}
      />
      
      {/* New Asset Management Modals */}
      <MaintenanceModal
        isOpen={showMaintenanceModal}
        onClose={() => {
          setShowMaintenanceModal(false);
          setSelectedAsset(null);
        }}
        asset={selectedAsset}
        isITManager={isITManager}
      />
      
      <OffboardingModal
        isOpen={showOffboardingModal}
        onClose={() => { setShowOffboardingModal(false); setOffboardInitialUser(null); fetchOffboardingCases(); }}
        allUsers={allUsers}
        initialUser={offboardInitialUser}
        onAssetsUpdated={fetchAssets}
      />
      
      <AcknowledgmentModal
        isOpen={showAcknowledgmentModal}
        onClose={() => {
          setShowAcknowledgmentModal(false);
          setSelectedAssignment(null);
        }}
        assignment={selectedAssignment}
        onAcknowledged={fetchAssets}
      />
      
      <QRScannerModal
        isOpen={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        onAssetFound={(assetId) => viewAssetDetail(assetId)}
      />
      
      <AnnouncementReadsModal
        isOpen={!!readsAnnouncementId}
        onClose={() => setReadsAnnouncementId(null)}
        announcementId={readsAnnouncementId}
        apiCall={apiCall}
      />
      
      {loadingTicketDetail && (
        <div className="modal-overlay">
          <div className="loading-spinner">Loading ticket details...</div>
        </div>
      )}

      {/* NO-NATIVE-POPUPS-001 — TitleFlow confirmation modal */}
      <ConfirmActionModal
        open={!!confirmAction}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.confirmLabel}
        tone="danger"
        theme="dark"
        onCancel={() => setConfirmAction(null)}
        onConfirm={async () => {
          try {
            await confirmAction.run();
          } catch (err) {
            toast.error(err.message || 'Action failed');
          }
          setConfirmAction(null);
        }}
      />
    </div>
  );
};

export default ITDashboard;
